/**
 * Material-change notice logic (pure, no network, no LLM).
 *
 * Index methodology administration (S&P / MSCI practice) distinguishes
 * MATERIAL from non-material methodology changes. A material change must give
 * stakeholders a minimum advance-notice window between when the change is
 * announced (notice date) and when it takes effect (effective date), and
 * generates a stakeholder notice. Non-material changes are unconstrained.
 */

/** Minimum advance notice, in days, a material change must give. */
export const MIN_NOTICE_DAYS = 30;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export interface NoticeWindowInput {
  /** Whether the change is classified material. Non-material → always ok. */
  material: boolean;
  /** ISO date (YYYY-MM-DD) the change was announced. Defaults to `now`. */
  noticeDate?: string;
  /** ISO date (YYYY-MM-DD) the change takes effect. Required when material. */
  effectiveDate?: string;
  /** ISO date (YYYY-MM-DD) for "today"; defaults to the current date. */
  now?: string;
}

export interface NoticeWindowResult {
  /** True when the notice window satisfies the requirement (or non-material). */
  ok: boolean;
  /** Whole days of notice given (announcement → effective). 0 when undefined. */
  daysNotice: number;
  /** Required minimum days (always MIN_NOTICE_DAYS). */
  requiredDays: number;
  /** Human, lowercase-leaning explanation. */
  reason: string;
}

/** Parse a YYYY-MM-DD date as UTC midnight; null when unparseable. */
function parseDay(value: string | undefined): number | null {
  if (!value) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (!m) return null;
  const ms = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(ms) ? null : ms;
}

function todayIso(): string {
  return new Date().toISOString().split("T")[0];
}

/**
 * Whole days of notice between announcement and effective date. Floored so a
 * partial day never rounds up to satisfy the window.
 */
export function noticeWindow(input: NoticeWindowInput): NoticeWindowResult {
  const requiredDays = MIN_NOTICE_DAYS;

  if (!input.material) {
    return {
      ok: true,
      daysNotice: 0,
      requiredDays,
      reason: "non-material change",
    };
  }

  const effective = parseDay(input.effectiveDate);
  if (effective === null) {
    return {
      ok: false,
      daysNotice: 0,
      requiredDays,
      reason: "material change needs an effective date",
    };
  }

  const announced =
    parseDay(input.noticeDate) ?? parseDay(input.now ?? todayIso())!;
  const daysNotice = Math.floor((effective - announced) / MS_PER_DAY);
  const ok = daysNotice >= requiredDays;

  return {
    ok,
    daysNotice,
    requiredDays,
    reason: ok
      ? `${daysNotice} days notice · ok`
      : `${daysNotice} days notice · needs ${requiredDays}`,
  };
}

export interface DraftNoticeInput {
  title: string;
  version: string;
  effectiveDate?: string;
  changeLog: string;
}

/**
 * Draft a plain, monospace-friendly stakeholder notice from the publish
 * metadata and change-log summary. Lowercase-leaning by design.
 */
export function draftStakeholderNotice(input: DraftNoticeInput): string {
  const title = input.title.trim() || "methodology";
  const version = input.version.trim();
  const when = (input.effectiveDate ?? "").trim() || "a date to be confirmed";
  const summary = input.changeLog.trim() || "- (no summary provided)";
  const subject = [title, version].filter(Boolean).join(" ");
  return [
    "notice of methodology change",
    "",
    `${subject} will take effect ${when}.`,
    "",
    "summary of changes:",
    summary,
  ].join("\n");
}
